/* eslint-disable react/no-did-update-set-state */
import * as React from 'react';
import classnames from 'classnames';
import chunk from 'lodash/chunk';
import isEqual from 'lodash/isEqual';
import kebabCase from 'lodash/kebabCase';

import ContentCardContext from 'components/cards/content-card/context/ContentCardContext';
import NotableLikesCard from 'components/cards/notable-likes-card/NotableLikesCard';

import NotableLikesArrow from './components/arrow/NotableLikesArrow';

import './notable-likes.scss';

import type {
  ContentCardItem,
  ContentCardType
} from 'components/cards/content-card/types';
import type {
  NotableLikesItem
} from './types/NotableLikes.type';
import type {
  ReportingMetaInfoIGStorySnippetType
} from 'modules/reporting/types';

type Props = {
  cards: Array<NotableLikesItem>,
  posts: Array<ContentCardItem>,
  type: ContentCardType,
  igStorySnippetType?: ReportingMetaInfoIGStorySnippetType,
  onNoPostClick: () => void
};

type State = {
  currentPosition: number,
  panes: Array<Array<NotableLikesItem>>
};

const CARDS_PER_PANE = 4;

class NotableLikes extends React.PureComponent<Props, State> {
  state = {
    currentPosition: 0,
    panes: chunk(this.props.cards, CARDS_PER_PANE)
  };

  componentDidUpdate(prevProps: Props) {
    const { cards } = this.props;

    if (!isEqual(cards, prevProps.cards)) {
      this.setState({
        panes: chunk(cards, CARDS_PER_PANE)
      });
    }
  }

  handleScroll = (event: React.MouseEvent<HTMLElement>): void => {
    const target = event?.currentTarget;
    const direction = target?.dataset.direction || 'prev';

    this.setState(state => ({
      currentPosition: direction === 'next'
        ? state.currentPosition + 1
        : state.currentPosition - 1
    }));
  };

  renderArrow(direction: 'prev' | 'next', isDisabled: boolean = false): React.ReactNode | undefined | null {
    const { cards } = this.props;

    if (cards.length <= CARDS_PER_PANE) {
      return null;
    }

    return (
      <NotableLikesArrow
        direction={direction}
        isDisabled={isDisabled}
        onScroll={this.handleScroll}
      />
    );
  }

  renderItems(): React.ReactNode {
    const { igStorySnippetType, onNoPostClick } = this.props;
    const { panes, currentPosition } = this.state;

    return (
      <div className="notable-likes__cards">
        {
          panes.map((pane, index) => {
            const paneClasses = classnames('notable-likes__pane', {
              'notable-likes__pane--is-visible': currentPosition === index
            });

            return (
              <div
                key={`notable-likes__pane--${index}`}
                className={paneClasses}
              >
                {
                  (pane || []).map(item => (
                    <div
                      key={`notable-likes__card--${item.contentCard.index}`}
                      className="notable-likes__card"
                    >
                      <NotableLikesCard
                        creatorFollower={item.creatorFollower}
                        channel={item.channel}
                        contentCard={{
                          ...item.contentCard,
                          igStorySnippetType
                        }}
                        onNoPostClick={onNoPostClick}
                      />
                    </div>
                  ))
                }
              </div>
            );
          })
        }
      </div>
    );
  }

  render(): React.ReactNode {
    const { posts, type } = this.props;
    const { panes, currentPosition } = this.state;

    const containerClasses = classnames('notable-likes', {
      [`notable-likes--is-${kebabCase(type)}`]: Boolean(type)
    });

    return (
      <ContentCardContext.Provider value={{ posts }}>
        <div className={containerClasses}>
          {this.renderArrow('prev', currentPosition === 0)}
          {this.renderArrow('next', currentPosition === panes.length - 1)}
          {this.renderItems()}
        </div>
      </ContentCardContext.Provider>
    );
  }
}

export default NotableLikes;
